/**
 * LegendaryReveal.js
 *
 * Phase 4 (June 2026) — the "wow, it's legendary!" banner shown inside a scan
 * result when the scanned body turns out to be a rare wonder world.
 *
 * Props: { legendary } — the `scan_data.legendary` block from the scan API:
 *   { is_legendary, archetype, archetype_name, blurb, lore,
 *     discovered_by, first_discovery, bounty, bounty_awarded }
 */

import React from 'react';
import { Gem, Sparkles, CircleDot, Flame, Crown, Trophy } from 'lucide-react';

const ARCHETYPE_THEME = {
  crystalline: { Icon: Gem, glow: '34,211,238', from: '#0e7490', accent: 'text-cyan-200', ring: 'border-cyan-300/40' },
  bioluminescent: { Icon: Sparkles, glow: '52,211,153', from: '#047857', accent: 'text-emerald-200', ring: 'border-emerald-300/40' },
  ringed: { Icon: CircleDot, glow: '196,181,253', from: '#6d28d9', accent: 'text-violet-200', ring: 'border-violet-300/40' },
  volcanic: { Icon: Flame, glow: '251,146,60', from: '#c2410c', accent: 'text-orange-200', ring: 'border-orange-300/40' },
};

export function LegendaryReveal({ legendary }) {
  if (!legendary?.is_legendary) return null;
  const theme = ARCHETYPE_THEME[legendary.archetype] || ARCHETYPE_THEME.crystalline;
  const { Icon } = theme;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${theme.ring} p-5`}
      style={{
        background: `linear-gradient(135deg, ${theme.from}33, rgba(8,11,18,0.7))`,
        boxShadow: `0 0 36px rgba(${theme.glow},0.28)`,
      }}
      data-testid="legendary-reveal"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(120px 80px at 12% 0%, rgba(${theme.glow},0.5), transparent)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-4 h-4 text-yellow-300" />
          <span className="text-[11px] tracking-[0.35em] uppercase text-yellow-300/90 font-semibold">
            Legendary World Discovered
          </span>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center border border-white/15"
            style={{ background: `rgba(${theme.glow},0.14)` }}
          >
            <Icon className={`w-7 h-7 ${theme.accent}`} />
          </div>
          <div>
            <div className={`text-xl font-bold ${theme.accent}`} data-testid="legendary-archetype-name">
              {legendary.archetype_name}
            </div>
            <div className="text-xs text-slate-300/80">{legendary.blurb}</div>
          </div>
        </div>

        {legendary.lore && (
          <p className="mt-4 text-sm leading-relaxed italic text-slate-200/90" data-testid="legendary-lore">
            “{legendary.lore}”
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {legendary.bounty_awarded && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-yellow-400/15 border border-yellow-300/40 px-3 py-1 text-xs font-semibold text-yellow-200"
              data-testid="legendary-bounty"
            >
              <Trophy className="w-3.5 h-3.5" />
              Discovery bounty +{Number(legendary.bounty || 0).toLocaleString()} cr
            </span>
          )}
          {legendary.first_discovery ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs text-white/90">
              <Sparkles className="w-3.5 h-3.5" /> First in the registry — your name is on it!
            </span>
          ) : legendary.discovered_by ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 px-3 py-1 text-xs text-slate-300">
              Discovered by <span className="font-semibold text-slate-100">{legendary.discovered_by}</span>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default LegendaryReveal;
